import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import VoiceRecorder from "../components/VoiceRecorder";
import {
  getProficiencyAssessmentTask,
  evaluateProficiencyAssessment,
  generatePersonalizedLearningPlan,
  getSpeechAnalysisForPlanTask,
} from "../services/englishPracticeService";
import Loader from "../components/Loader";
import { ChartBarIcon } from "../components/Icons";
import { useInterviewContext } from "../contexts/InterviewContext";

const EnglishPracticePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const voiceRecorderPracticeSessionRef = useRef();
  const voiceRecorderProficiencyEvaluationRef = useRef();
  const { setError: setGlobalError, clearGlobalError } = useInterviewContext(); // Renamed setError to avoid conflict
  const [currentPhase, setCurrentPhase] = useState("welcome");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [errorMessage, setLocalErrorMessage] = useState(null); // Renamed to avoid conflict
  const [sessionId, setSessionId] = useState(null);

  const errorDisplayRef = useRef(null);
  const phaseContainerRef = useRef(null);

  // State management
  const [assessmentTask, setAssessmentTask] = useState(null);
  const [proficiencyResult, setProficiencyResult] = useState(null);
  const [learningPlan, setLearningPlan] = useState(null);
  const [currentPlanSessionIndex, setCurrentPlanSessionIndex] = useState(0);
  const [currentPracticeTask, setCurrentPracticeTask] = useState(null);
  const [speechFeedback, setSpeechFeedback] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState("");

  const clearLocalError = useCallback(() => {
    setLocalErrorMessage(null);
    clearGlobalError();
  }, [clearGlobalError, setLocalErrorMessage]);

  const handleError = useCallback(
    (message, errorObj = null) => {
      console.error(message, errorObj);
      const displayMessage = errorObj?.message
        ? `${message}: ${errorObj.message}`
        : message;
      setLocalErrorMessage(displayMessage);
      setGlobalError(displayMessage); // Assuming this context error is for a global banner
      if (errorDisplayRef.current) {
        errorDisplayRef.current.focus();
      }
    },
    [setLocalErrorMessage, setGlobalError],
  );

  useEffect(() => {
    if (phaseContainerRef.current) {
      phaseContainerRef.current.focus();
    }
  }, [currentPhase]);

  // Initial Assessment Task Fetch
  useEffect(() => {
    const redoSessionId = searchParams.get("redoSessionId");

    if (redoSessionId) {
      searchParams.delete("redoSessionId");
      setSearchParams(searchParams);
    }
    if (currentPhase === "assessment_task_loading" || redoSessionId) {
      clearLocalError();
      setLoadingMessage("Generating your proficiency assessment task...");

      getProficiencyAssessmentTask(redoSessionId)
        .then((task) => {
          const proficiencyAssessmentTask = JSON.parse(
            task.proficiencyAssessmentTask,
          );
          setAssessmentTask(proficiencyAssessmentTask);
          setSessionId(task.sessionId);
          setCurrentPhase("assessment_recording");
        })
        .catch((err) => {
          handleError("Failed to load assessment task", err);
          setCurrentPhase("welcome"); // Or an error phase
        })
        .finally(() => setLoadingMessage(""));
    }
  }, [
    clearLocalError,
    currentPhase,
    handleError,
    searchParams,
    setSearchParams,
  ]);

  // Evaluate Assessment
  const handleAssessmentRecordingComplete = useCallback(
    async (recordedAudioUrl, durationSeconds, audioBlob) => {
      clearLocalError();
      setCurrentPhase("assessment_evaluating");
      setLoadingMessage("Evaluating your proficiency...");
      setAudioUrl(recordedAudioUrl);

      try {
        const evaluation = await evaluateProficiencyAssessment(
          sessionId,
          audioBlob,
        );
        setProficiencyResult(evaluation.data.evaluation);
        setTranscript(evaluation.data.richTranscriptionData.fullTranscript);
        setCurrentPhase("plan_generating");
      } catch (err) {
        handleError("Failed to evaluate proficiency", err);
        setCurrentPhase("assessment_recording"); // Or an error phase
      } finally {
        setLoadingMessage("");
      }
    },
    [clearLocalError, handleError, sessionId],
  );

  // Generate Learning Plan
  useEffect(() => {
    if (currentPhase === "plan_generating" && proficiencyResult) {
      clearLocalError();
      setLoadingMessage("Creating your personalized learning plan...");
      generatePersonalizedLearningPlan(sessionId)
        .then((plan) => {
          setLearningPlan(plan);
          setCurrentPlanSessionIndex(0);
          setCurrentPhase("practice_session_task");
        })
        .catch((err) => {
          handleError("Failed to generate learning plan", err);
          setCurrentPhase("assessment_evaluating"); // Or an error phase
        })
        .finally(() => setLoadingMessage(""));
    }
  }, [
    currentPhase,
    proficiencyResult,
    clearLocalError,
    handleError,
    sessionId,
  ]);

  // Set Current Practice Task
  useEffect(() => {
    if (
      currentPhase === "practice_session_task" &&
      learningPlan?.sessions?.length > 0
    ) {
      if (currentPlanSessionIndex < learningPlan.sessions.length) {
        setCurrentPracticeTask(learningPlan.sessions[currentPlanSessionIndex]);
        setSpeechFeedback(null);
        setAudioUrl(null);
        setTranscript("");
        clearLocalError();
      } else {
        setCurrentPhase("plan_complete");
      }
    }
  }, [currentPhase, learningPlan, currentPlanSessionIndex, clearLocalError]);

  // Handle Practice Task Transcription
  const handlePracticeTaskRecordingComplete = useCallback(
    async (recordedAudioUrl, durationSeconds, audioBlob) => {
      clearLocalError();
      setLoadingMessage("Analyzing your speech...");
      setSpeechFeedback(null); // Clear previous feedback
      setAudioUrl(recordedAudioUrl);

      try {
        const response = await getSpeechAnalysisForPlanTask(
          currentPracticeTask.id,
          "cleanTranscript", // Assuming this is a parameter for the backend
          proficiencyResult.estimatedProficiencyLevel,
          durationSeconds,
          audioBlob,
        );
        setTranscript(response.data.richTranscriptionData.fullTranscript);
        setSpeechFeedback(response.data.feedback);
        setCurrentPhase("practice_session_feedback");
      } catch (err) {
        handleError("Failed to get speech analysis", err);
        // Potentially revert to practice_session_task or show error more gracefully
      } finally {
        setLoadingMessage("");
      }
    },
    [currentPracticeTask, proficiencyResult, clearLocalError, handleError],
  );

  const handleNextTask = () => {
    setCurrentPlanSessionIndex((prev) => prev + 1);
    setCurrentPhase("practice_session_task");
  };

  const handleSpeak = (text) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "en-US";
      window.speechSynthesis.speak(utterance);
    } else {
      console.warn("Browser does not support text-to-speech.");
    }
  };

  const handleRetryTask = () => {
    setSpeechFeedback(null);
    setAudioUrl(null);
    setTranscript("");
    setCurrentPhase("practice_session_task");
    clearLocalError();
  };

  const handleRetryAssessment = () => {
    setAudioUrl(null);
    setTranscript("");
    setCurrentPhase("assessment_recording");
    clearLocalError();
  };

  const handleRestart = () => {
    clearLocalError();
    setAssessmentTask(null);
    setProficiencyResult(null);
    setLearningPlan(null);
    setCurrentPlanSessionIndex(0);
    setCurrentPracticeTask(null);
    setSpeechFeedback(null);
    setAudioUrl(null);
    setTranscript("");
    setCurrentPhase("welcome");
  };

  // ... (getTextForHighlighting, renderHighlightedText, getScoreColor, renderTaskContent remain complex and need careful accessibility review later)
  const getTextForHighlighting = () => {
    if (!currentPracticeTask) return "";
    // This function needs to be robust against missing content properties
    const content = currentPracticeTask.content || {};
    switch (currentPracticeTask.taskType) {
      case "read_aloud":
        return content.textToRead || "";
      case "sentence_repetition":
        return (content.sentencesToRepeat || []).join(" ");
      case "question_answer":
        return content.questionToAnswer || "";
      case "scenario_response":
        return content.scenarioDescription || "";
      case "topic_discussion_prompt":
        return content.discussionPrompt || content.topicPrompt || ""; // Added topicPrompt as a fallback
      default:
        return "";
    }
  };

  const renderHighlightedText = () => {
    const originalTextContent = getTextForHighlighting();
    if (!currentPracticeTask || !speechFeedback || !originalTextContent) {
      const content = currentPracticeTask?.content || {};
      return (
        <p className="whitespace-pre-wrap leading-relaxed text-slate-200 text-base sm:text-lg">
          {originalTextContent ||
            content.questionToAnswer ||
            content.scenarioDescription ||
            content.topicPrompt || // Consistent with getTextForHighlighting
            "No text to display for this task type."}
        </p>
      );
    }

    let textParts = [];
    let lastIndex = 0;
    const allIssues = [
      ...(speechFeedback.pronunciationIssues || []).map((p) => ({
        ...p,
        type: "pronunciation",
      })),
      ...(speechFeedback.omittedWords || []).map((o) => ({
        ...o,
        type: "omission",
      })),
    ];

    allIssues.sort(
      (a, b) =>
        (a.originalTextStartOffset || 0) - (b.originalTextStartOffset || 0),
    );

    allIssues.forEach((issue) => {
      const start = issue.originalTextStartOffset;
      const end = issue.originalTextEndOffset;

      if (start !== null && end !== null && start >= lastIndex && end > start) {
        if (start > lastIndex) {
          textParts.push(
            <span key={`text-${lastIndex}`}>
              {originalTextContent.substring(lastIndex, start)}
            </span>,
          );
        }
        let className =
          "font-medium transition-all duration-200 px-1 py-0.5 rounded-md";
        let tooltipText = "";
        if (issue.type === "pronunciation") {
          className +=
            " bg-amber-700/30 text-amber-300 border border-amber-600 shadow-sm";
          tooltipText = `Spoken: "${issue.spokenAttempt}". Suggestion: ${issue.suggestion}`;
        } else if (issue.type === "omission") {
          className +=
            " bg-red-700/30 text-red-300 line-through border border-red-600 shadow-sm";
          tooltipText = `Omitted word: "${issue.word}"`;
        }
        textParts.push(
          <span
            key={`issue-${start}-${issue.type}`}
            className={className}
            title={tooltipText} // Keep title for now, more complex solution later
            tabIndex={0} // Make focusable
          >
            {originalTextContent.substring(start, end)}
          </span>,
        );
        lastIndex = end;
      }
    });

    if (lastIndex < originalTextContent.length) {
      textParts.push(
        <span key={`text-${lastIndex}-end`}>
          {originalTextContent.substring(lastIndex)}
        </span>,
      );
    }
    return (
      <p className="whitespace-pre-wrap leading-relaxed text-slate-200 text-base sm:text-lg">
        {textParts.length > 0 ? textParts : originalTextContent}
      </p>
    );
  };

  const renderTaskContent = (task) => {
    const taskContent =
      typeof task.content === "string"
        ? JSON.parse(task.content) // Ensure this parsing is safe
        : task.content || {}; // Ensure task.content is not null/undefined

    if (task.taskType === "read_aloud" && taskContent.textToRead) {
      return speechFeedback ? (
        renderHighlightedText()
      ) : (
        <p className="whitespace-pre-wrap text-slate-200 text-base sm:text-lg leading-relaxed">
          {taskContent.textToRead}
        </p>
      );
    } else if (
      task.taskType === "sentence_repetition" &&
      Array.isArray(taskContent.sentencesToRepeat) // Check if it's an array
    ) {
      return speechFeedback ? (
        renderHighlightedText()
      ) : (
        <div className="space-y-3">
          {taskContent.sentencesToRepeat.map((sentence, i) => (
            <div key={i} className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 bg-indigo-500/30 text-indigo-300 rounded-full flex items-center justify-center text-sm font-medium">
                {i + 1}
              </span>
              <p className="text-slate-200 text-base sm:text-lg">{sentence}</p>
            </div>
          ))}
        </div>
      );
    } else if (
      task.taskType === "question_answer" &&
      taskContent.questionToAnswer
    ) {
      return (
        <div className="bg-blue-900/30 border-l-4 border-blue-600 p-4 rounded-r-lg">
          <p className="text-blue-200 font-medium text-base sm:text-lg">
            Question: {taskContent.questionToAnswer}
          </p>
        </div>
      );
    } else if (
      task.taskType === "scenario_response" &&
      taskContent.scenarioDescription
    ) {
      return (
        <div className="bg-purple-900/30 border-l-4 border-purple-600 p-4 rounded-r-lg">
          <p className="text-purple-200 font-medium text-base sm:text-lg">
            Scenario: {taskContent.scenarioDescription}
          </p>
        </div>
      );
    } else if (
      task.taskType === "topic_discussion_prompt" &&
      (taskContent.topicPrompt || taskContent.discussionPrompt) // Added discussionPrompt
    ) {
      return (
        <div className="bg-green-900/30 border-l-4 border-green-600 p-4 rounded-r-lg">
          <p className="text-green-200 font-medium text-base sm:text-lg">
            Topic: {taskContent.topicPrompt || taskContent.discussionPrompt}
          </p>
        </div>
      );
    } else {
      return (
        <p className="text-slate-300 text-base sm:text-lg">
          This task involves speaking freely. Follow the instructions provided.
        </p>
      );
    }
  };

  const renderContent = () => {
    if (loadingMessage) {
      return (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          ref={phaseContainerRef}
          tabIndex={-1}
          className="flex justify-center items-center min-h-[300px] sm:min-h-[400px] outline-none"
        >
          <Loader message={loadingMessage} />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div
          role="alert"
          aria-live="assertive"
          ref={errorDisplayRef}
          tabIndex={-1}
          className="mb-6 sm:mb-8 outline-none"
        >
          <div className="bg-red-900/30 border-l-4 border-red-600 rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <span aria-hidden="true" className="text-2xl">
                ⚠️
              </span>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-200 mb-1">
                  Something went wrong
                </h3>
                <p className="text-red-300 text-sm sm:text-base">
                  {errorMessage}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => {
                      clearLocalError();
                      if (currentPhase.includes("assessment")) {
                        handleRetryAssessment();
                      } else if (currentPhase.includes("practice")) {
                        handleRetryTask();
                      } else {
                        handleRestart();
                      }
                    }}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors duration-200"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleRestart}
                    className="px-3 py-1.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 text-sm font-medium transition-colors duration-200"
                  >
                    Start Over
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    switch (currentPhase) {
      case "welcome":
        return (
          <div
            ref={phaseContainerRef}
            tabIndex={-1}
            className="text-center bg-slate-800/70 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-700 p-6 sm:p-10 outline-none"
          >
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-4">
                <span aria-hidden="true" className="text-3xl">
                  🎯
                </span>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">
              Ready to Improve Your English?
            </h2>
            <p className="text-slate-300 mb-6 sm:mb-8 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Let's start with a quick assessment to understand your current
              level and create a personalized practice plan tailored just for
              you.
            </p>
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => setCurrentPhase("assessment_task_loading")}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 text-base sm:text-lg"
              >
                Begin Assessment
              </button>
              <button
                onClick={() => navigate("/dashboard/english-history")}
                className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold rounded-xl border border-slate-600 transition-all duration-300 flex items-center justify-center text-base" /* Adjusted padding & text size */
              >
                <ChartBarIcon aria-hidden="true" className="w-5 h-5 mr-2" />
                View Progress
              </button>
            </div>
          </div>
        );

      case "assessment_recording":
        if (!assessmentTask)
          return (
            <div
              role="status"
              aria-live="polite"
              aria-busy="true"
              ref={phaseContainerRef}
              tabIndex={-1}
              className="outline-none"
            >
              <Loader message="Loading assessment task..." />
            </div>
          );

        return (
          <div
            ref={phaseContainerRef}
            tabIndex={-1}
            className="space-y-6 outline-none"
          >
            <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-700 p-6 sm:p-8">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    {assessmentTask.title}
                  </h2>
                  <div className="bg-indigo-500/30 text-indigo-300 px-3 py-1 rounded-full text-sm font-medium">
                    Assessment
                  </div>
                </div>
                <p className="text-slate-400 text-sm sm:text-base italic leading-relaxed">
                  {assessmentTask.instructions}
                </p>
              </div>

              <div className="bg-slate-700/50 rounded-xl p-4 sm:p-6 mb-6 border border-slate-600">
                {renderTaskContent(assessmentTask)}
              </div>

              <div className="flex justify-center">
                <VoiceRecorder
                  ref={voiceRecorderProficiencyEvaluationRef}
                  onTranscriptionComplete={handleAssessmentRecordingComplete}
                  sendAudioDuration={true}
                />
              </div>
            </div>

            {(audioUrl || transcript) && (
              <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-700 p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-slate-200 mb-4 flex items-center">
                  <span aria-hidden="true" className="mr-2">
                    📝
                  </span>
                  Your Previous Attempt
                </h3>
                {audioUrl && (
                  <div className="mb-4">
                    <audio
                      controls
                      src={audioUrl}
                      className="w-full rounded-lg"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}
                {transcript && (
                  <div className="bg-slate-700 rounded-lg p-3 sm:p-4">
                    <p className="text-sm text-slate-400 mb-2">Transcript:</p>
                    <p className="text-slate-200 text-sm sm:text-base">
                      {transcript}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case "practice_session_task":
      case "practice_session_feedback":
        if (!currentPracticeTask && !loadingMessage && !errorMessage) {
          // Ensure not to overwrite loading/error
          return (
            <div ref={phaseContainerRef} tabIndex={-1} className="outline-none">
              <Loader message="Loading practice task..." />
            </div>
          );
        }
        return (
          <div
            ref={phaseContainerRef}
            tabIndex={-1}
            className="space-y-6 outline-none"
          >
            {currentPhase === "practice_session_task" &&
              currentPracticeTask && (
                <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-700 p-6 sm:p-8">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white">
                      {currentPracticeTask.title}
                    </h2>
                    <div className="text-indigo-300 font-medium bg-indigo-500/20 px-3 py-1 rounded-full text-sm">
                      Task {currentPlanSessionIndex + 1} of{" "}
                      {learningPlan.sessions.length}
                    </div>
                  </div>
                  {currentPracticeTask.instructions && (
                    <p className="text-slate-400 text-sm sm:text-base italic mb-6 leading-relaxed">
                      {currentPracticeTask.instructions}
                    </p>
                  )}
                  {renderTaskContent(currentPracticeTask)}
                  <div className="flex justify-center mt-6">
                    <VoiceRecorder
                      ref={voiceRecorderPracticeSessionRef}
                      onTranscriptionComplete={
                        handlePracticeTaskRecordingComplete
                      }
                      sendAudioDuration={true}
                    />
                  </div>
                </div>
              )}
            {currentPhase === "practice_session_feedback" && speechFeedback && (
              <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-700 p-6 sm:p-8 space-y-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-white text-center">
                  Performance Analysis
                </h3>

                {/* User's Transcript */}
                {transcript && (
                  <div>
                    <h4 className="text-lg font-semibold text-slate-100 mb-2">
                      Your Answer:
                    </h4>
                    <div className="p-3 bg-slate-700 rounded-md text-slate-200 whitespace-pre-wrap text-sm">
                      {transcript}
                    </div>
                  </div>
                )}
                {audioUrl && (
                  <div className="mb-4">
                    <audio
                      controls
                      src={audioUrl}
                      className="w-full rounded-lg"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}

                {/* Overall Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {speechFeedback.overallClarityDescription && (
                    <div className="bg-slate-700/50 p-4 rounded-lg">
                      <h5 className="text-sm font-medium text-slate-400 mb-1">
                        Overall Clarity
                      </h5>
                      <p className="text-lg font-semibold text-slate-100">
                        {speechFeedback.overallClarityDescription}
                      </p>
                    </div>
                  )}
                  {speechFeedback.speakingRate?.assessment && (
                    <div className="bg-slate-700/50 p-4 rounded-lg">
                      <h5 className="text-sm font-medium text-slate-400 mb-1">
                        Speaking Rate
                      </h5>
                      <p className="text-lg font-semibold text-slate-100">
                        {speechFeedback.speakingRate.assessment}
                      </p>
                    </div>
                  )}
                </div>

                {/* Key Areas to Improve */}
                {speechFeedback.keyAreasToImprove &&
                  speechFeedback.keyAreasToImprove.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-slate-100 mb-2">
                        💡 Key Areas to Improve:
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-slate-300 bg-slate-700/50 p-4 rounded-lg text-sm">
                        {speechFeedback.keyAreasToImprove.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Contextual Script/Prompt Display OR Detailed Script Analysis */}
                <div>
                  {currentPracticeTask.taskType === "read_aloud" ||
                  currentPracticeTask.taskType === "sentence_repetition" ? (
                    <>
                      <h4 className="text-lg font-semibold text-slate-100 mb-2">
                        Script Analysis:
                      </h4>
                      <div className="p-3 bg-slate-700/50 rounded-md">
                        {renderHighlightedText()}
                      </div>
                    </>
                  ) : (
                    <>
                      <h4 className="text-lg font-semibold text-slate-100 mb-2">
                        Original Prompt:
                      </h4>
                      <div className="p-3 bg-slate-700/40 rounded-md text-slate-300 italic text-sm">
                        {getTextForHighlighting() ||
                          "No prompt text available for this task."}
                      </div>
                    </>
                  )}
                </div>

                {/* Detailed Feedback Categories */}
                <div className="space-y-4">
                  {speechFeedback.pronunciationIssues &&
                    speechFeedback.pronunciationIssues.length > 0 && (
                      <div>
                        <h4 className="text-md font-semibold text-slate-200 mb-2">
                          🗣️ Pronunciation Practice:
                        </h4>
                        <ul className="space-y-2 text-sm">
                          {speechFeedback.pronunciationIssues.map(
                            (issue, index) => (
                              <li
                                key={index}
                                className="bg-slate-700/60 p-3 rounded-lg"
                              >
                                <p className="text-slate-300">
                                  Word:{" "}
                                  <span className="font-medium text-slate-100">
                                    {issue.word}
                                  </span>
                                </p>
                                <p className="text-slate-300">
                                  You said:{" "}
                                  <span className="italic text-amber-400">
                                    {issue.spokenAttempt}
                                  </span>
                                </p>
                                {issue.suggestion && (
                                  <div className="flex items-center space-x-2">
                                    <p className="text-slate-300">
                                      Suggestion:{" "}
                                      <span className="font-medium text-green-400">
                                        {issue.suggestion}
                                      </span>
                                    </p>
                                    <button
                                      onClick={() =>
                                        handleSpeak(issue.suggestion)
                                      }
                                      className="text-indigo-400 hover:text-indigo-300 p-1 rounded-full transition-colors"
                                      title="Listen to pronunciation"
                                    >
                                      <span aria-hidden="true">🔊</span>
                                    </button>
                                  </div>
                                )}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                  {(speechFeedback.omittedWords &&
                    speechFeedback.omittedWords.length > 0) ||
                  (speechFeedback.insertedWords &&
                    speechFeedback.insertedWords.length > 0) ? (
                    <div>
                      <h4 className="text-md font-semibold text-slate-200 mb-2">
                        ✍️ Word Choice:
                      </h4>
                      {speechFeedback.omittedWords &&
                        speechFeedback.omittedWords.length > 0 && (
                          <div className="mb-2">
                            <h5 className="text-sm font-medium text-slate-300 mb-1">
                              Omitted Words:
                            </h5>
                            <div className="flex flex-wrap gap-2">
                              {speechFeedback.omittedWords.map(
                                (item, index) => (
                                  <span
                                    key={index}
                                    className="bg-red-700/30 text-red-300 px-2 py-1 rounded-md text-xs"
                                  >
                                    {item.word}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      {speechFeedback.insertedWords &&
                        speechFeedback.insertedWords.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-slate-300 mb-1">
                              Inserted Words:
                            </h5>
                            <div className="flex flex-wrap gap-2">
                              {speechFeedback.insertedWords.map(
                                (item, index) => (
                                  <span
                                    key={index}
                                    className="bg-sky-700/30 text-sky-300 px-2 py-1 rounded-md text-xs"
                                  >
                                    {item.word}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  ) : null}

                  {speechFeedback.fluencyMarkers &&
                    speechFeedback.fluencyMarkers.length > 0 && (
                      <div>
                        <h4 className="text-md font-semibold text-slate-200 mb-2">
                          🌊 Fluency:
                        </h4>
                        <ul className="list-disc list-inside space-y-1 text-slate-300 bg-slate-700/60 p-3 rounded-lg text-sm">
                          {speechFeedback.fluencyMarkers.map((item, index) => (
                            <li key={index}>
                              {item.markerType}:{" "}
                              <span className="italic">"{item.instance}"</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {speechFeedback.grammarDeviations &&
                    speechFeedback.grammarDeviations.length > 0 && (
                      <div>
                        <h4 className="text-md font-semibold text-slate-200 mb-2">
                          📝 Grammar:
                        </h4>
                        <ul className="space-y-2 text-sm">
                          {speechFeedback.grammarDeviations.map(
                            (item, index) => (
                              <li
                                key={index}
                                className="bg-slate-700/60 p-3 rounded-lg"
                              >
                                {item.description && (
                                  <p className="text-indigo-300 font-medium mb-1">
                                    {item.description}
                                  </p>
                                )}
                                <p className="text-slate-300">
                                  Fragment:{" "}
                                  <span className="italic text-amber-400">
                                    "{item.fragment}"
                                  </span>
                                </p>
                                <p className="text-slate-300">
                                  Suggestion:{" "}
                                  <span className="font-medium text-green-400">
                                    {item.suggestion}
                                  </span>
                                </p>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                  {speechFeedback.positiveFeedback &&
                    speechFeedback.positiveFeedback.length > 0 && (
                      <div>
                        <h4 className="text-md font-semibold text-slate-200 mb-2">
                          👍 What Went Well:
                        </h4>
                        <ul className="list-disc list-inside space-y-1 text-green-300 bg-green-700/30 p-4 rounded-lg text-sm">
                          {speechFeedback.positiveFeedback.map(
                            (item, index) => (
                              <li key={index}>{item}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="mt-8 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={handleRetryTask}
                    className="px-6 py-3 bg-slate-600 text-white hover:bg-slate-500 rounded-xl font-medium transition-colors duration-200"
                  >
                    Practice Again
                  </button>
                  <button
                    onClick={handleNextTask}
                    className="px-6 py-3 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl font-medium transition-colors duration-200"
                  >
                    Next Task
                  </button>
                </div>
              </div>
            )}
            {currentPhase === "practice_session_feedback" &&
              !speechFeedback &&
              !loadingMessage &&
              !errorMessage && (
                <div
                  ref={phaseContainerRef}
                  tabIndex={-1}
                  className="outline-none"
                >
                  <Loader message="Loading feedback..." />
                </div>
              )}
          </div>
        );

      case "plan_complete":
        return (
          <div
            ref={phaseContainerRef}
            tabIndex={-1}
            className="text-center bg-slate-800/70 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-700 p-6 sm:p-10 outline-none"
          >
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-4">
                <span aria-hidden="true" className="text-3xl">
                  🎉
                </span>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">
              Congratulations!
            </h2>
            <p className="text-slate-300 mb-6 sm:mb-8 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              You've completed your personalized learning plan. Great job on
              improving your English speaking skills!
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto">
              <button
                onClick={handleRestart}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Start New
              </button>
              <button
                onClick={() => navigate("/dashboard/english-history")}
                className="px-6 py-3 bg-slate-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Review History
              </button>
              <button
                onClick={() =>
                  navigate(`/dashboard/english-history/${sessionId}`)
                }
                className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                View Details
              </button>
              <button
                onClick={() => {
                  setCurrentPlanSessionIndex(learningPlan.sessions.length - 1);
                  setCurrentPhase("practice_session_task");
                }}
                className="px-6 py-3 bg-amber-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Redo Last Task
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div
            ref={phaseContainerRef}
            tabIndex={-1}
            className="text-center py-8 outline-none"
          >
            <p className="text-slate-300">
              Something went wrong. Please refresh the page.
            </p>
          </div>
        );
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-slate-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              English Speaking Practice
            </h1>
            <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto">
              Improve your pronunciation, fluency, and confidence with
              personalized practice sessions
            </p>
          </div>
          {renderContent()}
        </div>
      </div>
    </main>
  );
};

export default EnglishPracticePage;
